import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import '../dashboard.css';
import { useLanguage } from '../../../contexts/LanguageContext';
import useModuleStats from '../sections/useModuleStats';
import StatusBar from '../sections/StatusBar';
import DashCard from './DashCard';
import { clampPercent, formatCount, formatShortDate } from './format';
import { projectStatusKeys, projectStatusLabel, projectStatusTone } from './projectStatus';

interface ProjectsOverviewCardProps {
  /** status -> count for the whole org (from /projects/statistics/). */
  byStatus: Record<string, number>;
  total: number;
  loading: boolean;
}

const ROWS = 8;

const RowSkeleton: React.FC = () => (
  <div className="dash-table-row" aria-hidden="true">
    <div className="dash-project-cell">
      <span className="dash-skel" style={{ width: 36, height: 36, borderRadius: 10 }} />
      <div className="dash-project-text" style={{ flex: 1 }}>
        <span className="dash-skel" style={{ height: 12, width: '60%' }} />
        <span className="dash-skel" style={{ height: 10, width: '35%' }} />
      </div>
    </div>
    <span className="dash-skel" style={{ height: 20, width: 64, borderRadius: 99 }} />
    <span className="dash-skel" style={{ height: 6 }} />
    <span className="dash-skel" style={{ height: 10, width: 56 }} />
  </div>
);

/**
 * Projects card: status distribution for the whole org, then the latest
 * projects filtered by status. Each tab queries the list endpoint with its
 * status, so a tab is never empty just because its rows fell outside the
 * newest few.
 */
const ProjectsOverviewCard: React.FC<ProjectsOverviewCardProps> = ({ byStatus, total, loading }) => {
  const { isRTL } = useLanguage();
  const [filter, setFilter] = useState<string>('all');

  const statusParam = filter === 'all' ? '' : `&status=${encodeURIComponent(filter)}`;
  const list = useModuleStats<any>(`/projects/?page_size=${ROWS}&ordering=-updated_at${statusParam}`);
  const rows: any[] = Array.isArray(list.data) ? list.data : list.data?.results || [];

  const keys = projectStatusKeys(byStatus);
  const distribution = keys.map((k) => ({
    key: k,
    label: projectStatusLabel(k, isRTL),
    count: byStatus[k] ?? 0,
    color: projectStatusTone(k).bar,
  }));
  const tabs = [
    { key: 'all', label: isRTL ? 'الكل' : 'All', count: total },
    ...keys.map((k) => ({ key: k, label: projectStatusLabel(k, isRTL), count: byStatus[k] ?? 0 })),
  ];

  const projectName = (p: any): string => (isRTL && p.title_ar ? p.title_ar : p.title) || p.project_id || '';

  return (
    <DashCard
      className="dash-card-wide"
      title={isRTL ? 'المشاريع' : 'Projects'}
      caption={isRTL ? 'توزيع الحالات وأحدث المشاريع' : 'Status distribution and latest projects'}
      to="/projects"
      flushHead
    >
      <div style={{ padding: '18px 22px 6px' }}>
        {loading ? (
          <div className="dash-dist" aria-hidden="true">
            <span className="dash-skel" style={{ height: 10, borderRadius: 99 }} />
            <span className="dash-skel" style={{ height: 12, width: '55%' }} />
          </div>
        ) : total > 0 ? (
          <StatusBar items={distribution} />
        ) : null}
      </div>

      <div className="dash-tabs" role="tablist" aria-label={isRTL ? 'تصفية حسب الحالة' : 'Filter by status'}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={filter === tab.key}
            className={classNames('dash-tab', { 'is-active': filter === tab.key })}
            onClick={() => setFilter(tab.key)}
          >
            {tab.label}
            <span className="dash-tab-count">{loading ? '…' : formatCount(tab.count)}</span>
          </button>
        ))}
      </div>

      <div className="dash-table-scroll">
        <div className="dash-table" role="table">
          <div className="dash-table-row dash-table-head" role="row">
            <span role="columnheader">{isRTL ? 'المشروع' : 'Project'}</span>
            <span role="columnheader">{isRTL ? 'الحالة' : 'Status'}</span>
            <span role="columnheader">{isRTL ? 'التقدم' : 'Progress'}</span>
            <span role="columnheader">{isRTL ? 'آخر تحديث' : 'Last update'}</span>
          </div>

          {list.loading ? (
            Array.from({ length: 3 }).map((_, i) => <RowSkeleton key={i} />)
          ) : list.error ? (
            <div className="dash-empty-row">
              {isRTL ? 'تعذّر تحميل المشاريع' : 'Could not load projects'}
            </div>
          ) : rows.length === 0 ? (
            <div className="dash-empty-row">
              {filter === 'all'
                ? (isRTL ? 'لا توجد مشاريع حتى الآن' : 'No projects yet')
                : (isRTL ? 'لا توجد مشاريع بهذه الحالة' : 'No projects with this status')}
            </div>
          ) : (
            rows.map((p) => {
              const tone = projectStatusTone(p.status);
              const progress = clampPercent(Number(p.progress_percentage));
              const name = projectName(p);
              return (
                <Link key={p.id} to={`/projects/${p.id}`} className="dash-table-row" role="row">
                  <div className="dash-project-cell" role="cell">
                    <span className="dash-initial" aria-hidden="true">{name.trim().charAt(0)}</span>
                    <div className="dash-project-text">
                      <span className="dash-project-name" title={name}>{name}</span>
                      {p.project_id && <span className="dash-project-code" dir="ltr">{p.project_id}</span>}
                    </div>
                  </div>
                  <div role="cell">
                    <span
                      className="dash-pill"
                      style={{ background: tone.bg, color: tone.fg, ['--dash-pill-dot' as any]: tone.bar }}
                    >
                      {projectStatusLabel(p.status, isRTL)}
                    </span>
                  </div>
                  <div className="dash-progress-cell" role="cell">
                    <div className="dash-bar" aria-hidden="true">
                      <span style={{ width: `${progress}%`, background: tone.bar }} />
                    </div>
                    <span>{progress}%</span>
                  </div>
                  <span className="dash-date" role="cell">{formatShortDate(p.updated_at, isRTL)}</span>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </DashCard>
  );
};

export default ProjectsOverviewCard;
